import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { MatSelect } from '@angular/material/select';

/**
 * Search field rendered as the first child of a `<mat-select>` panel.
 *
 * `mat-select` has no built-in filtering, so the PrimeNG `[filter]="true"` dropdowns
 * replaced during the Angular 22 migration lost their search box. Drop this in and
 * filter the options with `selectFilter`:
 *
 * ```html
 * <mat-select formControlName="state">
 *   <app-select-search #stateSearch placeholder="Search states"></app-select-search>
 *   <mat-option *ngFor="let s of states | selectFilter: stateSearch.query : ['name']" [value]="s.name">
 *     {{ s.name }}
 *   </mat-option>
 * </mat-select>
 * ```
 *
 * Reading `query` off a template reference keeps host components free of filter state.
 */
@Component({
  selector: 'app-select-search',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './select-search.component.html',
  styleUrls: ['./select-search.component.scss'],
})
export class SelectSearchComponent implements OnInit {
  @Input() placeholder = 'Search...';

  /** Current search text. Read it from a template ref to drive `selectFilter`. */
  @Input() query = '';

  @Output() queryChange = new EventEmitter<string>();

  @ViewChild('searchInput') private searchInput?: ElementRef<HTMLInputElement>;

  private readonly parentSelect = inject(MatSelect, { optional: true });
  private readonly destroyRef = inject(DestroyRef);

  /**
   * mat-select moves focus to its own panel when it opens, which lands after any
   * timeout scheduled from this component's construction. Waiting for openedChange
   * means the search box takes focus once the panel has settled, rather than racing it.
   */
  ngOnInit(): void {
    this.parentSelect?.openedChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(opened => {
      if (opened) {
        this.searchInput?.nativeElement?.focus();
      }
    });
  }

  /**
   * Marking this component and its ancestors dirty is what the template-bound event
   * already does, so the host's `selectFilter` still re-runs under OnPush.
   */
  onInput(event: Event): void {
    this.query = (event.target as HTMLInputElement)?.value ?? '';
    this.queryChange.emit(this.query);
  }

  /**
   * mat-select treats printable keys as typeahead and would steal them from the input,
   * so only the keys that drive the panel itself are allowed through.
   */
  onKeydown(event: KeyboardEvent): void {
    const passthrough = ['ArrowDown', 'ArrowUp', 'Enter', 'Escape', 'Tab'];
    if (!passthrough.includes(event.key)) {
      event.stopPropagation();
    }
  }

  clear(): void {
    this.query = '';
    this.queryChange.emit('');
    this.searchInput?.nativeElement?.focus();
  }
}
